/**
 * Date Default Utils
 * 날짜 기본값 계산 유틸리티
 *
 * @description
 * 필터 기본값 설정에 자주 사용되는 날짜 계산 함수 모음
 * - 오늘 날짜, N일 전 날짜 계산
 * - 주차 시작/종료일 계산
 * - YYYY-MM-DD 형식 반환
 */

// ============================================================
// 기본 날짜 계산 함수
// ============================================================

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환
 *
 * @returns YYYY-MM-DD 형식의 오늘 날짜
 *
 * @example
 * getToday() // '2024-01-15'
 */
export const getToday = (): string => {
    const today = new Date();
    return formatToDateString(today);
};

/**
 * N일 전 날짜를 YYYY-MM-DD 형식으로 반환
 *
 * @param days 며칠 전인지 (기본값: 7)
 * @returns YYYY-MM-DD 형식의 날짜
 *
 * @example
 * getDaysAgo(7) // 7일 전 날짜
 * getDaysAgo() // 7일 전 날짜 (기본값)
 */
export const getDaysAgo = (days = 7): string => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return formatToDateString(date);
};

/**
 * Date 객체를 YYYY-MM-DD 형식 문자열로 변환
 *
 * @param date Date 객체
 * @returns YYYY-MM-DD 형식 문자열
 */
export const formatToDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// ============================================================
// 기본 날짜 범위 반환
// ============================================================

/**
 * 기본 날짜 범위 타입
 */
export interface DateRange {
    startDate: string;
    endDate: string;
}

/**
 * 기본 7일 날짜 범위 반환 (오늘 ~ 7일 전)
 *
 * @returns { startDate, endDate } 형태의 날짜 범위
 *
 * @example
 * const { startDate, endDate } = getDefaultDateRange();
 */
export const getDefaultDateRange = (): DateRange => ({
    startDate: getDaysAgo(7),
    endDate: getToday(),
});

/**
 * N일 날짜 범위 반환
 *
 * @param days 기간 (일 단위)
 * @returns { startDate, endDate } 형태의 날짜 범위
 */
export const getDateRangeByDays = (days: number): DateRange => ({
    startDate: getDaysAgo(days),
    endDate: getToday(),
});

// ============================================================
// 주차 계산 함수
// ============================================================

/**
 * 주차 날짜 범위 타입
 */
export interface WeekRange {
    startDate: string;  // 수요일
    endDate: string;    // 화요일
}

/**
 * 현재 주차의 수요일~화요일 날짜 범위 반환
 * 부릉 정산 주기: 수요일 ~ 화요일
 *
 * @returns { startDate, endDate } 형태의 주차 범위
 *
 * @example
 * const { startDate, endDate } = getCurrentWeekRange();
 */
export const getCurrentWeekRange = (): WeekRange => {
    const today = new Date();
    const currentDay = today.getDay(); // 0: 일요일, 3: 수요일

    // 이번 주 수요일 계산 (오늘이 수요일이면 0, 목요일이면 -1, ...)
    const diffToWed = (currentDay + 7 - 3) % 7;
    const wednesday = new Date(today);
    wednesday.setDate(today.getDate() - diffToWed);

    // 다음 주 화요일 (수요일 + 6일)
    const tuesday = new Date(wednesday);
    tuesday.setDate(wednesday.getDate() + 6);

    return {
        startDate: formatToDateString(wednesday),
        endDate: formatToDateString(tuesday),
    };
};

/**
 * 지정된 날짜가 속한 주차의 수요일~화요일 범위 반환
 *
 * @param date 기준 날짜 (Date 객체 또는 YYYY-MM-DD 문자열)
 * @returns { startDate, endDate } 형태의 주차 범위
 */
export const getWeekRangeByDate = (date: Date | string): WeekRange => {
    const targetDate = typeof date === 'string' ? new Date(date) : date;
    const currentDay = targetDate.getDay();

    const diffToWed = (currentDay + 7 - 3) % 7;
    const wednesday = new Date(targetDate);
    wednesday.setDate(targetDate.getDate() - diffToWed);

    const tuesday = new Date(wednesday);
    tuesday.setDate(wednesday.getDate() + 6);

    return {
        startDate: formatToDateString(wednesday),
        endDate: formatToDateString(tuesday),
    };
};
